import { BaseApiClient } from './base-client';

export interface LinuxUser {
  id: number;
  username: string;  // Username without prefix
  service_id: number;
  permissions: Record<string, any>;
  created_at: string;
}

export interface SubuserLoginRequest {
  service_id: number;
  username: string;
  password: string;
  device_name?: string;
}

export interface SubuserLoginResponse {
  token: string;
  expires_at: string;
  permissions: Record<string, any>;
  service_id: number;
}

// Keep the old interface names for compatibility but use LinuxUser type
export interface Subuser extends LinuxUser {
  is_active: boolean;
  two_factor_enabled: boolean;
  last_login?: string;
  active_tokens: number;
}

export class SubusersApiClient extends BaseApiClient {
  async list(serviceId: number): Promise<LinuxUser[]> {
    return await this.get<LinuxUser[]>(`/api/v1/subusers/${serviceId}`);
  }

  // Authentication endpoint for desktop app
  async login(data: SubuserLoginRequest): Promise<SubuserLoginResponse> {
    return await this.post<SubuserLoginResponse>('/api/v1/subusers/auth/login', data);
  }

  // Keep these methods for backward compatibility but they'll return errors
  async create(serviceId: number, data: any): Promise<any> {
    throw new Error('Creating users should be done via the Users section');
  }

  async update(serviceId: number, subuserId: number, data: any): Promise<any> {
    throw new Error('User updates not supported');
  }

  async deleteSubuser(serviceId: number, subuserId: number): Promise<any> {
    throw new Error('Deleting users should be done via the Users section');
  }

  async enable2FA(serviceId: number, subuserId: number): Promise<any> {
    throw new Error('2FA not supported for Linux users');
  }

  async verify2FA(serviceId: number, subuserId: number, totpCode: string): Promise<any> {
    throw new Error('2FA not supported for Linux users');
  }

  async disable2FA(serviceId: number, subuserId: number): Promise<any> {
    throw new Error('2FA not supported for Linux users');
  }

  async listTokens(serviceId: number, subuserId: number): Promise<any[]> {
    return [];
  }

  async revokeToken(serviceId: number, subuserId: number, tokenId: number): Promise<any> {
    throw new Error('Token management not supported');
  }

  // Legacy method name kept for compatibility
  async loginWithParent(serviceId: number, username: string, password: string, totpCode?: string, deviceName?: string): Promise<SubuserLoginResponse> {
    return this.login({
      service_id: serviceId,
      username,
      password,
      device_name: deviceName
    });
  }
}